/* eslint-disable default-param-last */
/* eslint-disable import/prefer-default-export */
import { isAfter, isBefore, isSameDay, isValid, startOfDay, subDays } from 'date-fns';
import { find, isDate, isEmpty, isNaN, isNil, reduce, replace, some, split, trim } from 'lodash';

import {
  creditCardRegex,
  emailRfc5322Regex,
  npiRegex,
  phoneNumberRegex,
  zipCodeRegex,
} from 'common/constants/regex';
import usStateAbbreviation from 'common/constants/usStateAbbreviation';

export const GRAPHQL_INT_MAX = 2147483647;

export const composeValidations = (validators) => (value, allValues, meta) => {
  if (!validators?.length) {
    return undefined;
  }

  let result;
  some(validators, (validator) => {
    result = validator(value, allValues, meta);
    return result;
  });

  return result;
};

export const validateRequired =
  (errorMessage = 'Required') =>
  (value) =>
    trim(value) ? undefined : errorMessage;

export const validateRequiredCheckbox =
  (errorMessage = 'Required') =>
  (value) =>
    value ? undefined : errorMessage;

export const validateMinLength =
  (errorMessage = 'Too Short', minLength) =>
  (value) =>
    !value || trim(value).length >= minLength ? undefined : errorMessage;

export const validateMaxLength =
  (errorMessage = 'Too Long', maxLength) =>
  (value) =>
    !value || trim(value).length <= maxLength ? undefined : errorMessage;

const getByPath = (allValues, path) =>
  reduce(split(path, '.'), (result, prop) => result?.[prop], allValues);

export const validateCombinedAddressLength =
  (
    line1Field = 'addressLine1',
    line2Field = 'addressLine2',
    maxLength = 70,
    errorMessage = `Combined address lines must be ${maxLength} characters or fewer`
  ) =>
  (value, allValues) => {
    const combinedLength =
      trim(getByPath(allValues, line1Field)).length + trim(getByPath(allValues, line2Field)).length;

    return combinedLength <= maxLength ? undefined : errorMessage;
  };

export const validateValueWithRegex =
  (errorMessage = 'Invalid Value', regex) =>
  (value) =>
    !value || regex.test(value) ? undefined : errorMessage;

export const validateZipCode =
  (errorMessage = 'Zip Code must be 5 or 9 digits') =>
  (value) =>
    !value || zipCodeRegex.test(value) ? undefined : errorMessage;

// This regex should allow 1800 numbers and normal 10digits phone numbers
// anything above 10 digits would require a + infront of it. this includes 1800 numbers
export const validatePhoneNumber =
  (errorMessage = 'Invalid Phone Number') =>
  (value) =>
    !value || phoneNumberRegex.test(value) ? undefined : errorMessage;

export const validateMustMatch =
  (errorMessage = 'Does not match', match) =>
  (value, allValues) =>
    value === allValues[match] ? undefined : errorMessage;

export const validateEmail =
  (errorMessage = 'Invalid Email') =>
  (value) =>
    !value || emailRfc5322Regex.test(value) ? undefined : errorMessage;

export const validateEmailArray =
  (errorMessage = 'Invalid Email(s)') =>
  (values) => {
    if (isEmpty(values)) return undefined;
    return values.every((value) => emailRfc5322Regex.test(value)) ? undefined : errorMessage;
  };

export const validateMinNumber =
  (errorMessage = 'Too low', minValue) =>
  (value) =>
    !Number(value) || Number(value) >= minValue ? undefined : errorMessage;

export const validateNumber =
  (errorMessage = 'Provide a valid number', startsWith) =>
  (value) => {
    if (!value) return undefined;
    return value === `${startsWith}` || !!Number(value) ? undefined : errorMessage;
  };

export const validateMaxNumber =
  (errorMessage = 'Too high', maxValue) =>
  (value) =>
    !Number(value) || Number(value) <= maxValue ? undefined : errorMessage;

export const validatePositiveInteger =
  (errorMessage = 'Enter a positive whole number') =>
  (value) => {
    if (value === undefined || value === null || String(value).trim() === '') return undefined;
    const n = Number.parseInt(String(value).trim(), 10);
    if (Number.isNaN(n)) return errorMessage;
    if (n < 1) return 'Must be at least 1';
    if (String(n) !== String(value).trim()) return errorMessage;
    return undefined;
  };

export const validateMinDate =
  (errorMessage = 'Invalid min date', minDate) =>
  (value) =>
    !value || (isValid(value) && isAfter(value, minDate)) ? undefined : errorMessage;

/** Validates that the date is today or a future date (not in the past). */
export const validateDateNotInPast =
  (errorMessage = 'Date must be today or a future date.') =>
  (value) => {
    if (!value || !isValid(value)) return undefined;
    const minDate = startOfDay(subDays(new Date(), 1));
    return isAfter(value, minDate) ? undefined : errorMessage;
  };

export const validateDateConstraint =
  (errorMessage = 'Invalid date', comparator, dateParam) =>
  (value) => {
    if (!value || !dateParam) return undefined;
    if (!isValid(value) || !isValid(dateParam)) return undefined;

    const validateRules = {
      isBefore: (a, b) => isBefore(a, b),
      isAfter: (a, b) => isAfter(a, b),
    };

    return validateRules[comparator](value, dateParam) ? undefined : errorMessage;
  };

export const validateMaxDate =
  (errorMessage = 'Invalid max date', maxDate) =>
  (value) =>
    !value || (isValid(value) && isBefore(value, maxDate)) ? undefined : errorMessage;

export const validateDate =
  (errorMessage = 'Invalid date') =>
  (value) =>
    !value || isValid(value) ? undefined : errorMessage;

export const validatePhoneAllowedInState =
  (errorMessage = 'Phone notifications are not allowed in the selected state', key) =>
  (value, allValues) => {
    const props = split(key, '.');
    const state = reduce(props, (result, prop) => result[prop], allValues);

    if (value && state) {
      return find(usStateAbbreviation, { abbreviation: state }).isSMSAllowed
        ? undefined
        : errorMessage;
    }

    return undefined;
  };

export const validateCreditCardNumber =
  (errorMessage = 'Invalid card') =>
  (value) =>
    !value || creditCardRegex.test(replace(value, /[^0-9]/gm, '')) ? undefined : errorMessage;

export const validateExpirationDate =
  (errorMessage = 'Invalid Expiration') =>
  (value) => {
    if (!value) return undefined;

    const [month, year] = split(value, '/');
    const date = `20${year}-${month}-01`;

    return isDate(new Date(date)) && isBefore(new Date(), new Date(date))
      ? undefined
      : errorMessage;
  };

export const validateQuantity =
  (errorMessage = 'Quantity should be greater than 0') =>
  (value) =>
    +value >= 1 ? undefined : errorMessage;

export const validateQuantities = (fieldName) => (value, allValues) => {
  const prescribed = Number(allValues.prescribedQuantity);
  const dispensed = Number(allValues.dispensedQuantity);

  if (fieldName === 'prescribedQuantity' && prescribed < dispensed) {
    return 'Prescribed quantity cannot be less than dispensed quantity';
  }

  if (fieldName === 'dispensedQuantity' && dispensed > prescribed) {
    return 'Dispensed quantity cannot be higher than prescribed quantity';
  }

  return undefined;
};

export const validateRefills = (errorMessage, dispensedQuantity) => (value) => {
  return value < dispensedQuantity ? errorMessage : undefined;
};

export const validateLessThan =
  (errorMessage = 'Too high', maxValue) =>
  (value) =>
    isNil(value) || (!Number.isNaN(value) && Number(value) < maxValue) ? undefined : errorMessage;

export const validateLessThanOrEqual =
  (errorMessage = 'Too high', maxValue) =>
  (value) =>
    isNil(value) || (!Number.isNaN(value) && Number(value) <= maxValue) ? undefined : errorMessage;

export const validateGreaterThan =
  (errorMessage = 'Too low', minValue) =>
  (value) =>
    isNil(value) || (!isNaN(Number(value)) && Number(value) > minValue) ? undefined : errorMessage;

export const validateGreaterThanOrEqual =
  (errorMessage = 'Too low', minValue) =>
  (value) =>
    isNil(value) || (!isNaN(Number(value)) && Number(value) >= minValue) ? undefined : errorMessage;

export const validateNPI =
  (errorMessage = 'Invalid NPI') =>
  (value) =>
    !value || npiRegex.test(value) ? undefined : errorMessage;

export const validateTimeString =
  (errorMessage = 'Enter a time between 00:00 and 23:59') =>
  (value) => {
    if (!value) return undefined;
    return /^([01]\d|2[0-3]):([0-5]\d)$/.test(value) ? undefined : errorMessage;
  };

export const validateTimeNotInFuture =
  (errorMessage = 'Time cannot be in the future', dateFieldName) =>
  (value, allValues) => {
    if (!value || !dateFieldName) return undefined;

    const referenceDate = allValues?.[dateFieldName];
    if (!referenceDate || !isValid(new Date(referenceDate))) return undefined;

    const referenceDateObj = new Date(referenceDate);
    const now = new Date();

    if (!isSameDay(referenceDateObj, now)) return undefined;

    const [hours, minutes] = value.split(':');
    const selectedDateTime = new Date(referenceDateObj);
    selectedDateTime.setHours(Number(hours), Number(minutes), 0, 0);

    return selectedDateTime.getTime() > now.getTime() ? errorMessage : undefined;
  };

export const validateNDC =
  (errorMessage = 'Invalid NDC format') =>
  (value) => {
    if (!value) return undefined;

    const trimmedValue = trim(value);
    if (!trimmedValue) return undefined;

    const hasHyphens = trimmedValue.includes('-');

    if (hasHyphens) {
      const hyphenPatterns = [
        /^\d{4}-\d{4}-\d{2}$/, // 4-4-2 format (10 digits)
        /^\d{5}-\d{3}-\d{2}$/, // 5-3-2 format (10 digits)
        /^\d{5}-\d{4}-\d{1}$/, // 5-4-1 format (10 digits)
        /^\d{5}-\d{4}-\d{2}$/, // 5-4-2 format (11 digits)
      ];

      const isValidHyphenated = hyphenPatterns.some((pattern) => pattern.test(trimmedValue));

      if (!isValidHyphenated) {
        return errorMessage;
      }

      if (!/^[\d-]+$/.test(trimmedValue)) {
        return errorMessage;
      }
    } else {
      if (!/^\d+$/.test(trimmedValue)) {
        return errorMessage;
      }
      if (trimmedValue.length !== 10 && trimmedValue.length !== 11) {
        return 'NDC must be 10 or 11 digits only';
      }
    }

    return undefined;
  };
